"use client";

import { useState, useCallback, useEffect, useMemo } from "react";
import { useFacetDefinitions } from "modules/collections/details/components/facets/facetPresentation";
import type { FacetSettings } from "utils/facet-settings";
import {
  getFacetSettingsFromClientCookie,
  setFacetSettingsCookie,
} from "utils/facet-settings";
import type { FacetContext } from "modules/common/model/models";

function getDefaultSettings(defaultOrder: readonly string[]): FacetSettings {
  return { order: [...defaultOrder], hidden: [] };
}

export function useFacetSettings(
  storageKey: string,
  facetContext: FacetContext = "collection"
) {
  const definitions = useFacetDefinitions(facetContext);
  const defaultOrder = useMemo(
    () => definitions.map((definition) => definition.key),
    [definitions]
  );
  const defaultOrderKey = defaultOrder.join("\u0000");
  const [settings, setSettings] = useState<FacetSettings>(() =>
    getDefaultSettings(defaultOrder)
  );

  // Load from cookie on mount
  useEffect(() => {
    setSettings(getFacetSettingsFromClientCookie(storageKey, defaultOrder));
  }, [defaultOrder, defaultOrderKey, storageKey]);

  const persist = useCallback(
    (next: FacetSettings) => {
      setSettings(next);
      setFacetSettingsCookie(storageKey, next);
    },
    [storageKey]
  );

  const moveUp = useCallback(
    (key: string) => {
      setSettings((prev) => {
        const idx = prev.order.indexOf(key);
        if (idx <= 0) return prev;
        const next = [...prev.order];
        [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
        const updated = { ...prev, order: next };
        setFacetSettingsCookie(storageKey, updated);
        return updated;
      });
    },
    [storageKey]
  );

  const moveDown = useCallback(
    (key: string) => {
      setSettings((prev) => {
        const idx = prev.order.indexOf(key);
        if (idx < 0 || idx >= prev.order.length - 1) return prev;
        const next = [...prev.order];
        [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
        const updated = { ...prev, order: next };
        setFacetSettingsCookie(storageKey, updated);
        return updated;
      });
    },
    [storageKey]
  );

  const toggleHidden = useCallback(
    (key: string) => {
      setSettings((prev) => {
        const isHidden = prev.hidden.includes(key);
        const hidden = isHidden
          ? prev.hidden.filter((k) => k !== key)
          : [...prev.hidden, key];
        const updated = { ...prev, hidden };
        setFacetSettingsCookie(storageKey, updated);
        return updated;
      });
    },
    [storageKey]
  );

  const reorder = useCallback(
    (newOrder: string[]) => {
      setSettings((prev) => {
        const updated = { ...prev, order: newOrder };
        setFacetSettingsCookie(storageKey, updated);
        return updated;
      });
    },
    [storageKey]
  );

  const resetToDefaults = useCallback(() => {
    const defaults = getDefaultSettings(defaultOrder);
    persist(defaults);
  }, [defaultOrder, persist]);

  return {
    settings,
    moveUp,
    moveDown,
    toggleHidden,
    reorder,
    resetToDefaults,
  };
}
